import { z } from 'zod'

export const adminMeResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    roles: z.array(z.string()),
  }),
  profile: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
  }),
})

export const adminBusinessListItemSchema = z.object({
  id: z.string().uuid(),
  legalName: z.string(),
})

export const adminBusinessListResponseSchema = z.array(adminBusinessListItemSchema)

const count = () => z.number().int().nonnegative()

/**
 * How the plan feature is used right now. Deleted plans are left out everywhere; "today" and
 * the daily series are calendar days in Europe/Vienna.
 */
export const adminPlanStatsResponseSchema = z.object({
  /** When the snapshot was taken. */
  generatedAt: z.string().datetime(),
  /** Today's date in Europe/Vienna. */
  today: z.string().date(),
  plans: z.object({
    /** Plans stored right now; a plan is purged 30 days after it expires. */
    total: count(),
    /** Plans people created in the last 24 hours, automatic repeats of recurring plans left out. */
    createdLast24h: count(),
    /** Plans people created in the last 7 days, automatic repeats of recurring plans left out. */
    createdLast7d: count(),
    /** Plans people created in the last 30 days, automatic repeats of recurring plans left out. */
    createdLast30d: count(),
    /** Plans not expired that are dated today or later, or have no date. */
    upcoming: count(),
    /** Upcoming plans dated today. */
    happeningToday: count(),
    /** Upcoming plans whose chat is still quiet: a 1:1 meetup that never opened a group chat. */
    upcomingQuietChat: count(),
    /** Upcoming plans with an open group chat. */
    upcomingOpenChat: count(),
    /** Upcoming plans linked to an event. */
    upcomingWithEvent: count(),
    /** Upcoming plans linked to a listing. */
    upcomingWithListing: count(),
  }),
  people: z.object({
    /**
     * Distinct people who joined a plan as going, maybe or en route in the last 7 days; a creator
     * counts as joining their own plan.
     */
    joinedLast7d: count(),
    /** The same over the last 30 days. */
    joinedLast30d: count(),
    /** People in `joinedLast7d` still on a web guest account. */
    guestsJoinedLast7d: count(),
    /** Mean people going, maybe or en route per upcoming plan; null without upcoming plans. */
    averagePerUpcomingPlan: z.number().nonnegative().nullable(),
    /** Median of the same; null without upcoming plans. */
    medianPerUpcomingPlan: z.number().nonnegative().nullable(),
  }),
  invites: z.object({
    /** Invites to another person sent in the last 7 days; join requests are not counted. */
    sentLast7d: count(),
    /** Those of `sentLast7d` accepted so far. */
    acceptedLast7d: count(),
    /** `acceptedLast7d / sentLast7d` from 0 to 1; null when none were sent. */
    acceptedShareLast7d: z.number().min(0).max(1).nullable(),
  }),
  chat: z.object({
    /** Messages people sent in plan chats in the last 24 hours; system lines are not counted. */
    messagesLast24h: count(),
    /** The same over the last 7 days. */
    messagesLast7d: count(),
    /** Plan chats with at least one such message in the last 7 days. */
    activeChatsLast7d: count(),
  }),
  assistant: z.object({
    /** Plan chat messages of the last 7 days the assistant acted on. */
    handledMessagesLast7d: count(),
    /** Actions it took on them, such as a poll, a list item, a vote or changed plan fields. */
    actionsLast7d: count(),
    /** Handled messages whose actions were undone. */
    undoneLast7d: count(),
  }),
  /** The last 14 days, oldest first, ending today. */
  daily: z.array(
    z.object({
      day: z.string().date(),
      /** Plans people created that day, counted like `plans.createdLast24h`. */
      plansCreated: count(),
      /** Distinct people who joined a plan that day, counted like `people.joinedLast7d`. */
      peopleJoined: count(),
    }),
  ),
})

/**
 * How many people use Pulsify. Deleted accounts and the official Pulsify account are left out
 * everywhere; "today" and the daily series are calendar days in Europe/Vienna.
 */
export const adminUserStatsResponseSchema = z.object({
  /** When the snapshot was taken. */
  generatedAt: z.string().datetime(),
  /** Today's date in Europe/Vienna. */
  today: z.string().date(),
  accounts: z.object({
    /** Signed-up accounts; web guests are not counted. */
    total: count(),
    /**
     * Accounts created in the last 24 hours. A web guest who signs up later keeps the time it
     * first opened a shared plan.
     */
    newLast24h: count(),
    /** The same over the last 7 days. */
    newLast7d: count(),
    /** The same over the last 30 days. */
    newLast30d: count(),
  }),
  guests: z.object({
    /** Web guests: people who opened a shared plan in the browser without signing up. */
    total: count(),
    /** Web guests created in the last 7 days. */
    newLast7d: count(),
  }),
  active: z.object({
    /**
     * Accounts that sent a chat message or answered a plan in the last 24 hours. The creator's
     * own place in an automatic repeat of a recurring plan does not count.
     */
    last24h: count(),
    /** The same over the last 7 days. */
    last7d: count(),
    /** The same over the last 30 days. */
    last30d: count(),
  }),
  /** The last 14 days, oldest first, ending today. */
  daily: z.array(
    z.object({
      day: z.string().date(),
      /** Accounts created that day, counted like `accounts.newLast24h`. */
      signups: count(),
      /** Accounts active that day, counted like `active.last24h`. */
      active: count(),
    }),
  ),
})

const guestWindows = <T extends z.ZodTypeAny>(value: () => T) =>
  z.object({ total: value(), last7d: value(), last30d: value() })

const share = () => z.number().min(0).max(1).nullable()

/**
 * How many web guests (people without an account who checked in to a Live Room by code or link)
 * use Live Rooms and how many of them have an account by now. A guest counts in a window when
 * they checked in as a guest within it; deleted profiles are left out. Weeks start on Monday in
 * Europe/Vienna.
 */
export const adminLiveRoomGuestStatsResponseSchema = z.object({
  /** When the snapshot was taken. */
  generatedAt: z.string().datetime(),
  /** Today's date in Europe/Vienna. */
  today: z.string().date(),
  /** Distinct people who checked in to a Live Room as a guest: all time, last 7 and 30 days. */
  guests: guestWindows(count),
  /**
   * Of those, the people who have an account now, whether they signed up from the guest session
   * or signed in to an account they already had.
   */
  converted: guestWindows(count),
  /** `converted / guests` per window from 0 to 1; null without guests. */
  conversionRate: guestWindows(share),
  /**
   * Hours from a converted guest's first guest check-in to the account. Only measured where the
   * account was made within two days of a guest check-in.
   */
  timeToAccount: z.object({
    /** Converted guests with a measured time. */
    measured: count(),
    averageHours: z.number().nonnegative().nullable(),
    medianHours: z.number().nonnegative().nullable(),
  }),
  /**
   * The last 12 weeks, oldest first, ending with the current week. Each guest counts once, in the
   * week of their first guest check-in.
   */
  weekly: z.array(
    z.object({
      /** Monday the week starts on. */
      week: z.string().date(),
      guests: count(),
      /** Guests of that week who have an account now. */
      converted: count(),
    }),
  ),
  /** Up to 10 events with the most guests checked in over the last 30 days, most first. */
  topEvents: z.array(
    z.object({
      eventId: z.string().uuid(),
      title: z.string(),
      /** Distinct guests who checked in to the event's Live Room in the last 30 days. */
      guestJoins: count(),
      /** Those of them who have an account now. */
      converted: count(),
    }),
  ),
})

/** Why a recent signup looks worth a second look. Order is the order shown to the admin. */
export const signupRiskFlagValues = [
  'burst_signup',
  'shared_email_domain',
  'unconfirmed_email',
  'duplicate_display_name',
  'no_date_of_birth',
  'date_of_birth_changed',
  'no_avatar',
] as const

export type SignupRiskFlag = (typeof signupRiskFlagValues)[number]

export const adminRecentSignupSchema = z.object({
  profileId: z.string().uuid(),
  displayName: z.string(),
  /** Null when the auth record carries no address, e.g. a phone-only or guest account. */
  email: z.string().nullable(),
  emailConfirmed: z.boolean(),
  /** The provider the account signed up through, e.g. `email` or `apple`; null if unrecorded. */
  provider: z.string().nullable(),
  /** Where the person says they live (ISO 3166-1 alpha-2); null for older accounts. */
  country: z.string().nullable(),
  /** Age from the stored date of birth; null when none is set. */
  age: z.number().int().nonnegative().nullable(),
  isGuest: z.boolean(),
  hasAvatar: z.boolean(),
  hasPhone: z.boolean(),
  createdAt: z.string().datetime(),
  lastSignInAt: z.string().datetime().nullable(),
  riskFlags: z.array(z.enum(signupRiskFlagValues)),
})

/**
 * The newest accounts with the signals needed to judge whether they are real people.
 * `averageAge` covers only the accounts that have a date of birth, so `dobCoverage` says how
 * much of the window that average actually speaks for.
 */
export const adminRecentSignupsResponseSchema = z.object({
  generatedAt: z.string().datetime(),
  /** How many days back the window reaches. */
  windowDays: count(),
  signups: z.array(adminRecentSignupSchema),
  /** Accounts created in the window, which can exceed the number of rows returned. */
  windowTotal: count(),
  ageStats: z.object({
    averageAge: z.number().nonnegative().nullable(),
    /** Accounts in the window with a date of birth. */
    withDateOfBirth: count(),
    /** `withDateOfBirth / windowTotal` from 0 to 1; null on an empty window. */
    dobCoverage: share(),
  }),
  /** How many accounts in the window carry each flag, for the summary row. */
  flagCounts: z.record(z.enum(signupRiskFlagValues), count()),
})

/** Which cell size the activity map is asking for; the floor scales with it. */
export const geoZoomTierValues = ['europe', 'country', 'city'] as const
export type GeoZoomTier = (typeof geoZoomTierValues)[number]

/** Which signal a map cell counts. */
export const geoLayerValues = ['usage', 'scans', 'signups', 'live'] as const
export type GeoLayer = (typeof geoLayerValues)[number]

export const geoActivityCellSchema = z.object({
  /** Centre of the cell. */
  lat: z.number(),
  lng: z.number(),
  /** Feed opens counted in the cell over the window. */
  opens: count(),
  /** Distinct people counted in the cell over the window. */
  people: count(),
  /** Live Room scans in the cell over the window. */
  scans: count(),
  /** People checked in right now, from the live presence snapshot. */
  live: count(),
})

/**
 * Aggregated activity per map cell. Cells below the tier's floor are dropped and only counted
 * in `suppressedCells`, so a cell can never narrow to one identifiable person at close zoom.
 * No cell carries a user reference of any kind.
 */
export const adminGeoActivityResponseSchema = z.object({
  generatedAt: z.string().datetime(),
  /** The tier that actually produced these cells. */
  tier: z.enum(geoZoomTierValues),
  /** The tier that was asked for; differs from `tier` when the floor would have hidden everything. */
  requestedTier: z.enum(geoZoomTierValues),
  /** Cell edge in kilometres for the tier that answered. */
  cellKm: z.number().positive(),
  /** The smallest number of people a cell must hold to be shown at this tier. */
  floor: count(),
  windowDays: count(),
  cells: z.array(geoActivityCellSchema),
  /** Cells that held data but fell below the floor. */
  suppressedCells: count(),
  /** Named places ranked by activity, for the list beside the map. */
  topCities: z.array(
    z.object({
      city: z.string(),
      scans: count(),
      live: count(),
    }),
  ),
  /** Accounts per country of residence, for the europe-tier choropleth. */
  countries: z.array(z.object({ code: z.string(), accounts: count() })),
})

/** One area ranked by how much demand it shows relative to what there is to do there. */
export const growthAreaSchema = z.object({
  /** Centre of the area. */
  lat: z.number(),
  lng: z.number(),
  /** Distinct people who opened the app here in the window. */
  people: count(),
  opens: count(),
  /** Live Room scans here in the window. */
  scans: count(),
  /** Published events here that have not finished. */
  events: count(),
  /** Published venues here. */
  listings: count(),
  /** The closest named place we already know of, from our own events and venues. */
  nearestPlace: z.string().nullable(),
  /** How far that place is, in kilometres; null when nothing of ours is near. */
  nearestPlaceKm: z.number().nonnegative().nullable(),
  /** Demand per unit of supply — see `readGrowthOpportunities` for the formula. */
  opportunityScore: z.number().nonnegative(),
})

/** One place's Live Room funnel, which is where a venue's real value shows up. */
export const growthVenueSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  city: z.string(),
  scans: count(),
  joins: count(),
  accounts: count(),
  /** `accounts / scans` from 0 to 1; null when nothing was scanned. */
  scanToAccount: share(),
})

/**
 * Where to spend the next hour. Areas rank demand against the supply already there, so a place
 * people open the app in with nothing to do ranks above a busy city that is already served.
 * Venues rank by how many of the people who scanned them became accounts.
 */
export const adminGrowthOpportunitiesResponseSchema = z.object({
  generatedAt: z.string().datetime(),
  windowDays: count(),
  /** Area edge in kilometres; areas are the map's country-tier cells. */
  cellKm: z.number().positive(),
  areas: z.array(growthAreaSchema),
  venues: z.array(growthVenueSchema),
})

export type AdminMeResponse = z.infer<typeof adminMeResponseSchema>
export type AdminBusinessListItem = z.infer<typeof adminBusinessListItemSchema>
export type AdminBusinessListResponse = z.infer<typeof adminBusinessListResponseSchema>
export type AdminPlanStatsResponse = z.infer<typeof adminPlanStatsResponseSchema>
export type AdminUserStatsResponse = z.infer<typeof adminUserStatsResponseSchema>
export type AdminLiveRoomGuestStatsResponse = z.infer<typeof adminLiveRoomGuestStatsResponseSchema>
export type AdminRecentSignup = z.infer<typeof adminRecentSignupSchema>
export type AdminRecentSignupsResponse = z.infer<typeof adminRecentSignupsResponseSchema>
export type GeoActivityCell = z.infer<typeof geoActivityCellSchema>
export type AdminGeoActivityResponse = z.infer<typeof adminGeoActivityResponseSchema>
export type GrowthArea = z.infer<typeof growthAreaSchema>
export type GrowthVenue = z.infer<typeof growthVenueSchema>
export type AdminGrowthOpportunitiesResponse = z.infer<
  typeof adminGrowthOpportunitiesResponseSchema
>
